import { useState, useEffect } from 'react';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from '../context/AuthContext';
import { useToast } from './use-toast';

export const usePushNotifications = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    const ua = window.navigator.userAgent || '';
    const iOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;
    setIsIOS(iOS);
    setIsStandalone(standalone);
    // No iOS, Web Push só funciona em PWA instalado (standalone) no iOS 16.4+
    const baseSupport = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(baseSupport && (!iOS || standalone));
  }, []);

  const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const getVapidPublicKey = async () => {
    try {
      const { data } = await supabase.functions.invoke('get-vapid-keys');
      return data?.publicKey;
    } catch (error) {
      console.error('Erro ao buscar VAPID key:', error);
      // Fallback para uma chave pública gerada (será implementada na edge function)
      return null;
    }
  };

  const subscribeUser = async () => {
    if (!isSupported || !user) {
      console.log('Push notifications não suportadas ou usuário não logado');
      return false;
    }

    setLoading(true);
    
    try {
      const registration = await navigator.serviceWorker.ready;
      
      // Verificar se já está subscrito
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        await saveSubscriptionToDatabase(existingSubscription);
        setIsSubscribed(true);
        setLoading(false);
        return true;
      }

      // Obter VAPID public key
      const vapidPublicKey = await getVapidPublicKey();
      if (!vapidPublicKey) {
        throw new Error('VAPID public key não encontrada');
      }

      // Criar nova subscription
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      await saveSubscriptionToDatabase(subscription);
      setIsSubscribed(true);
      
      toast({
        title: "Notificações ativadas!",
        description: "Você receberá notificações push sobre novos leads e imóveis.",
      });

      return true;
    } catch (error) {
      console.error('Erro ao subscrever para push notifications:', error);
      toast({
        title: "Erro ao ativar notificações",
        description: "Não foi possível ativar as notificações push.",
        variant: "destructive"
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const unsubscribeUser = async () => {
    if (!isSupported || !user) return false;

    setLoading(true);
    
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        await removeSubscriptionFromDatabase();
        setIsSubscribed(false);
        
        toast({
          title: "Notificações desativadas",
          description: "Você não receberá mais notificações push.",
        });
      }
      
      return true;
    } catch (error) {
      console.error('Erro ao cancelar subscription:', error);
      toast({
        title: "Erro ao desativar notificações",
        description: "Não foi possível desativar as notificações push.",
        variant: "destructive"
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const saveSubscriptionToDatabase = async (subscription: PushSubscription) => {
    try {
      if (!user?.id) throw new Error('Usuário não autenticado');

      // Extrair chaves de forma compatível com TS (getKey -> base64)
      const toBase64 = (buf: ArrayBuffer | null) =>
        buf ? btoa(String.fromCharCode(...new Uint8Array(buf))) : null;
      const p256dh = toBase64(subscription.getKey('p256dh'));
      const auth = toBase64(subscription.getKey('auth'));

      const subscriptionJson = subscription.toJSON?.() ?? {};

      const { error } = await (supabase as any)
        .from('push_subscriptions')
        .upsert({
          user_id: user.id,
          endpoint: subscription.endpoint,
          p256dh,
          auth,
          subscription_data: subscriptionJson,
        });

      if (error) throw error;
    } catch (error) {
      console.error('Erro ao salvar subscription:', error);
      throw error;
    }
  };

  const removeSubscriptionFromDatabase = async () => {
    try {
      if (!user?.id) throw new Error('Usuário não autenticado');
      const { error } = await (supabase as any)
        .from('push_subscriptions')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;
    } catch (error) {
      console.error('Erro ao remover subscription:', error);
      throw error;
    }
  };

  const checkSubscriptionStatus = async () => {
    if (!isSupported || !user) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
      // NÃO renovar automaticamente — isso cria endpoints duplicados toda vez que o app abre.
      // Renovação deve ser explícita pelo botão "Limpar e re-inscrever".
    } catch (error) {
      console.error('Erro ao verificar status da subscription:', error);
    }
  };

  useEffect(() => {
    if (user) {
      checkSubscriptionStatus();
    }
  }, [user, isSupported]);

  return {
    isSupported,
    isSubscribed,
    loading,
    isIOS,
    isStandalone,
    subscribeUser,
    unsubscribeUser,
    checkSubscriptionStatus
  };
};